import { useRef, useEffect, useState } from 'react';

export function CameraCapture({ onCapture, onClose }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [error, setError] = useState(null);
  const [isCameraReady, setIsCameraReady] = useState(false);
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    let stream = null;

    async function startCamera() {
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'environment', width: { ideal: 1280 }, height: { ideal: 720 } }
        });
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          setIsCameraReady(true);
        }
      } catch (err) {
        setError(err.message || 'Cannot access camera. Please check permissions.');
      }
    }

    startCamera();

    return () => {
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  function capturePhoto() {
    if (!videoRef.current || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const video = videoRef.current;
    const ctx = canvas.getContext('2d');

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    ctx.drawImage(video, 0, 0);

    canvas.toBlob((blob) => {
      setPreview(canvas.toDataURL('image/jpeg', 0.9));
      onCapture(blob);
    }, 'image/jpeg', 0.9);
  }

  function resetCapture() {
    setPreview(null);
  }

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      background: '#000',
      display: 'flex',
      flexDirection: 'column',
      zIndex: 10000,
    }}>
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
        {error ? (
          <div style={{ textAlign: 'center', color: '#fff', padding: '20px' }}>
            <div style={{ fontSize: '18px', marginBottom: '10px', color: '#ff6b6b' }}>📷 Camera Error</div>
            <div style={{ marginBottom: '20px', fontSize: '14px', color: '#ccc' }}>{error}</div>
            <button
              onClick={onClose}
              style={{
                padding: '10px 20px',
                borderRadius: '6px',
                background: '#4a90e2',
                color: '#fff',
                border: 'none',
                cursor: 'pointer',
                fontSize: '14px',
              }}
            >
              Close
            </button>
          </div>
        ) : preview ? (
          <img src={preview} alt="captured" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
        ) : (
          <video
            ref={videoRef}
            autoPlay
            playsInline
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        )}
      </div>

      <canvas ref={canvasRef} style={{ display: 'none' }} />

      <div style={{
        padding: '16px',
        background: 'rgba(0,0,0,0.8)',
        display: 'flex',
        gap: '12px',
        justifyContent: 'center',
        alignItems: 'center',
      }}>
        {preview ? (
          <>
            <button
              onClick={resetCapture}
              style={{
                padding: '12px 24px',
                borderRadius: '6px',
                background: 'transparent',
                color: '#fff',
                border: '1px solid #666',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 600,
              }}
            >
              ↺ Retake
            </button>
            <button
              onClick={onClose}
              style={{
                padding: '12px 24px',
                borderRadius: '6px',
                background: '#666',
                color: '#fff',
                border: 'none',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 600,
              }}
            >
              Close
            </button>
          </>
        ) : (
          <>
            <button
              onClick={capturePhoto}
              disabled={!isCameraReady}
              style={{
                padding: '12px 24px',
                borderRadius: '50%',
                width: '64px',
                height: '64px',
                background: '#4a90e2',
                color: '#fff',
                border: 'none',
                cursor: isCameraReady ? 'pointer' : 'not-allowed',
                fontSize: '28px',
                fontWeight: 600,
                opacity: isCameraReady ? 1 : 0.5,
              }}
            >
              ⊙
            </button>
            <button
              onClick={onClose}
              style={{
                padding: '12px 24px',
                borderRadius: '6px',
                background: 'transparent',
                color: '#fff',
                border: '1px solid #666',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 600,
              }}
            >
              Close
            </button>
          </>
        )}
      </div>
    </div>
  );
}
